/**
 * What a site page says about itself in its HTML, before any script runs: the title a browser tab and
 * a search result show; the description, picture and address a link preview draws; and a readable
 * copy of the page for anything that does not run JavaScript.
 *
 * Pure — no fetch, no DOM — so /api/site-page draws a page's HTML with it on the server and
 * `node --test` checks it. The site itself (SiteApp) draws the same page in the browser, and these
 * rules read a module the way it does (siteModel.headOf), so a preview card and the page agree.
 */
import { cf, idList, headOf, pageViewOf, resolveSiteCollection, sortSiteRows, statValue, resolveLink, itemPath, reachableIds, itemForPath,
  sitesOf, siteForPath, siteBaseOf, templateTypeOf } from './siteModel.js';
import { readSiteSettings, readPageSeo } from './siteSettings.js';
import { splitMentions } from '../../utils/mentions.js';

/** At most this many items of one collection are written into the readable copy. */
const MAX_LISTED = 200;

export function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// Built from character codes, so no escape sequence in this file can turn into the character it
// names: a raw U+2028 inside a regular expression literal is a syntax error.
const BACKSLASH = String.fromCharCode(92);
const UNSAFE_IN_SCRIPT = new RegExp(`[<${String.fromCharCode(0x2028)}${String.fromCharCode(0x2029)}]`, 'g');

/** A value as JSON that is safe inside a <script> element: no `</script>`, no line separators. */
export const scriptJson = (value) => JSON.stringify(value ?? null)
  .replace(UNSAFE_IN_SCRIPT, (c) => `${BACKSLASH}u${c.charCodeAt(0).toString(16).padStart(4, '0')}`);

/**
 * A public page's words with each mention as the name its writer gave it: `[[@id|Ana Ruiz]]` reads
 * "Ana Ruiz", `[[id|Regenerative Finance]]` "Regenerative Finance". A site is prose for anyone, so a
 * mention reads as a plain name, with no `@`, and its stored id never reaches a visitor or a crawler.
 * SiteApp reads its bodies and descriptions through this too, so a page and its preview agree.
 */
export function labelMentions(text) {
  const t = String(text ?? '');
  return t.includes('[[') ? splitMentions(t).map((p) => ('text' in p ? p.text : p.label)).join('') : t;
}

/** Markdown down to plain words; at most `max` characters, cut at a word, when `max` is given. */
export function plainText(markdown, max = 0) {
  // Mentions first, so the cut below can never land inside one and leave half an id behind.
  let t = labelMentions(markdown || '')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/[*_`>~]+/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (max && t.length > max) {
    const cut = t.slice(0, max - 1);
    t = `${cut.slice(0, Math.max(cut.lastIndexOf(' '), Math.floor(max * 0.6))).trim()}…`;
  }
  return t;
}

/**
 * A site's context for drawing its pages, from the /api/site payload: the same `ctx` SiteApp builds,
 * so addresses come out exactly as the page's own links make them. `siteId` picks one of the space's
 * sites (the primary when absent), and the base is that site's address unless one is given.
 */
export function siteContext(payload, { spaceSlug, base = null, siteId = null } = {}) {
  const rows = Array.isArray(payload?.rows) ? payload.rows : [];
  const rowsById = new Map(rows.map((r) => [r.id, r]));
  const typesById = new Map((payload?.types || []).map((t) => [t.id, t]));
  const sites = sitesOf(rows, payload?.spaceId || null);
  const siteRow = rowsById.get(siteId || payload?.siteId) || null;
  const reach = siteRow ? reachableIds({ siteRow, rows, typesById }) : new Set();
  return {
    spaceSlug, base: typeof base === 'string' ? base : (siteRow && spaceSlug ? siteBaseOf(spaceSlug, siteRow, sites) : null), sites,
    rows, rowsById, typesById, siteRow, reach,
    homeId: idList(cf(siteRow).home_page)[0] || null,
    spaceId: siteRow?.primary_space_id || payload?.spaceId || null,
    slugBySpace: payload?.spaces || {},
  };
}

/**
 * The item at a site address's segments — none for the home page — or null for one the site lacks. A
 * Page that draws each item of a type (`detail.page_by_type`) is null too: it has no item of its own to
 * show, and the browser tells a visitor it is not on the site, so its address is no page to index.
 */
export function pageAt(segments, ctx) {
  const row = segments.length ? itemForPath(segments, ctx) : ctx.rowsById.get(ctx.homeId) || null;
  return row && ctx.siteRow && templateTypeOf(row, readSiteSettings(cf(ctx.siteRow))) ? null : row;
}

/**
 * Which of the space's sites an address inside /<space>/site is on, and that site's page: `{ ctx, row }`.
 * The first segment names a later site by its slug; anything else is the primary's (siteForPath).
 */
export function sitePageAt(payload, segments, { spaceSlug }) {
  const rows = Array.isArray(payload?.rows) ? payload.rows : [];
  const { site, rest } = siteForPath(segments, sitesOf(rows, payload?.spaceId || null));
  const ctx = siteContext(payload, { spaceSlug, siteId: site?.id || null });
  return { ctx, row: pageAt(rest, ctx) };
}

/**
 * The title a page's tab and search result carry: its own SEO title when one is set, else the site's
 * name on its home page and "<page> — <site>" on any other. Shared with SiteApp, which sets the tab's
 * title as a visitor moves between pages, so the two never disagree.
 */
export function pageTitle(row, siteRow, isHome) {
  const own = row ? readPageSeo(pageViewOf(row)?.frame).title : '';
  const siteName = siteRow?.title || '';
  return own || (isHome || !row ? siteName : `${row.title} — ${siteName}`);
}

/**
 * A page's title, description and picture, and what search engines are asked. Whatever a page's SEO
 * settings say wins (PAGE_SEO_SCHEMA); whatever they leave empty is worked out, as it always was:
 *   - TITLE: pageTitle above. The preview card's own title (`heading`) is the SEO title too, else the
 *     page's name, else the site's.
 *   - DESCRIPTION: the page's own description, else the first words its sections show, else the
 *     site's description — plain text, at most 300 characters.
 *   - PICTURE: the first picture its sections show (a header's background usually), else the item's
 *     cover, else the site's share picture, else the item's logo, else the site's logo.
 *   - ICON: the site's tab icon, else its logo.
 *   - NOINDEX: the page's, or the whole site's.
 * An item page that is not a Page (an event, a venue) has no SEO settings of its own and is read from
 * its fields; the site's settings still apply to it.
 */
export function pageMeta(row, ctx) {
  const site = ctx.siteRow;
  const siteName = site?.title || '';
  const isHome = !row || row.id === ctx.homeId;
  const seo = row ? readPageSeo(pageViewOf(row)?.frame) : readPageSeo(null);
  const siteSeo = readSiteSettings(cf(site)).seo;
  const heads = (row ? pageViewOf(row)?.modules || [] : []).map((m) => headOf(m, ctx.rowsById));
  const words = seo.description || row?.description || heads.map((h) => h.body).find(Boolean) || row?.page_markdown || site?.description || '';
  return {
    title: pageTitle(row, site, isHome),
    heading: seo.title || (isHome ? siteName : row.title),
    siteName,
    description: plainText(words, 300),
    image: seo.image || heads.map((h) => h.background || h.image).find(Boolean) || row?.image_cover_url
      || siteSeo.share_image || cf(row).image_logo || cf(site).image_logo || null,
    icon: siteSeo.favicon || cf(site).image_logo || null,
    noindex: seo.noindex || siteSeo.noindex,
    verification: siteSeo.google_verification || null,
  };
}

/** The tags a link preview and a search engine read. Empty values write nothing. */
export function headTags({ description, canonical, image, siteName, heading, icon, noindex = false, verification = null }) {
  const meta = (attr, key, value) => (value ? `<meta ${attr}="${key}" content="${escapeHtml(value)}">` : '');
  return [
    meta('name', 'description', description),
    canonical ? `<link rel="canonical" href="${escapeHtml(canonical)}">` : '',
    noindex ? '<meta name="robots" content="noindex">' : '',
    meta('name', 'google-site-verification', verification),
    meta('property', 'og:type', 'website'),
    meta('property', 'og:site_name', siteName),
    meta('property', 'og:title', heading),
    meta('property', 'og:description', description),
    meta('property', 'og:url', canonical),
    meta('property', 'og:image', image),
    meta('name', 'twitter:card', image ? 'summary_large_image' : 'summary'),
    meta('name', 'twitter:title', heading),
    meta('name', 'twitter:description', description),
    meta('name', 'twitter:image', image),
    icon ? `<link rel="icon" href="${escapeHtml(icon)}">` : '',
  ].filter(Boolean).join('');
}

const linkTag = (href, label, external = false) =>
  `<a href="${escapeHtml(href)}"${external ? ' rel="noopener noreferrer"' : ''}>${escapeHtml(label)}</a>`;
/** A button as a link: where resolveLink sends it, marked when it leaves the site. */
const buttonTag = (link, label, ctx) => { const r = resolveLink(link, ctx); return linkTag(r.href, label, !!r.external); };
const paragraphs = (markdown) => String(markdown || '').split(/\n\s*\n/)
  .map((p) => plainText(p)).filter(Boolean).map((p) => `<p>${escapeHtml(p)}</p>`).join('');

/**
 * The page as plain, readable HTML: its navigation, then each section's words, its buttons as links,
 * the items of a collection it shows (linked where the site gives them a page), then the footer. It
 * goes in a <noscript>, for whatever reads the page without running it.
 */
export function pageReadableHtml(row, ctx) {
  const site = ctx.siteRow;
  // A nav entry with a URL of its own and no page view — a Link item — goes to that URL, as the live
  // header and footer send it (SiteApp's SiteFooter); a Share link, which has no share menu here, to the
  // site's home. Anything else is its page on the site.
  const navLinks = (ids) => idList(ids).map((id) => {
    const r = ctx.rowsById.get(id);
    if (!r) return '';
    const label = cf(r).nav_label || r.title || 'Untitled';
    return r.url && !cf(r).Views ? buttonTag({ href: r.url }, label, ctx) : linkTag(itemPath(ctx.spaceSlug, r, ctx.homeId, ctx), label);
  }).filter(Boolean).join(' ');
  const itemLink = (r) => (ctx.reach.has(r.id) ? linkTag(itemPath(ctx.spaceSlug, r, ctx.homeId, ctx), r.title || 'Untitled') : escapeHtml(r.title || 'Untitled'));
  const out = [];
  const header = navLinks(cf(site).header_links);
  if (header) out.push(`<nav>${header}</nav>`);
  out.push('<main>');
  const view = row ? pageViewOf(row) : null;
  if (!row) {
    out.push('<h1>Not on this site</h1>');
  } else if (view && view.modules.length) {
    const firstTitled = Boolean(headOf(view.modules[0], ctx.rowsById).title);
    if (!firstTitled) out.push(`<h1>${escapeHtml(row.title || '')}</h1>`);
    view.modules.forEach((m, i) => {
      const head = headOf(m, ctx.rowsById);
      const part = [];
      if (head.eyebrow) part.push(`<p>${escapeHtml(head.eyebrow)}</p>`);
      if (head.title) part.push(i === 0 ? `<h1>${escapeHtml(head.title)}</h1>` : `<h2>${escapeHtml(head.title)}</h2>`);
      if (head.body) part.push(paragraphs(head.body));
      if (head.aside) part.push(`<blockquote>${paragraphs(head.aside)}</blockquote>`);
      // A section's live numbers, as words: "26 calendars gathered", "140 upcoming events".
      const stats = (Array.isArray(m.stats) ? m.stats : []).map((st) => ({ st, n: statValue(st, ctx) })).filter((x) => x.n != null);
      if (stats.length) part.push(`<ul>${stats.map(({ st, n }) => `<li>${n} ${escapeHtml(n === 1 && st.one ? st.one : st.label || '')}</li>`).join('')}</ul>`);
      if (m.archetype === 'info' && Array.isArray(m.cards) && m.cards.length) {
        part.push(`<ul>${m.cards.map((c) => {
          const link = c.link ? ` ${buttonTag(c.link, c.link.label || c.title || '', ctx)}` : '';
          return `<li><strong>${escapeHtml(c.title || '')}</strong>${c.text ? ` ${escapeHtml(plainText(c.text))}` : ''}${link}</li>`;
        }).join('')}</ul>`);
      }
      if (m.archetype === 'collection' && m.source?.collection) {
        const coll = ctx.rowsById.get(m.source.collection);
        const items = coll ? sortSiteRows(resolveSiteCollection(coll, ctx.rows, ctx.typesById), m.source.sortBy) : [];
        if (items.length) {
          part.push(`<ul>${items.slice(0, MAX_LISTED).map((r) => `<li>${itemLink(r)}${r.description ? ` — ${escapeHtml(plainText(r.description, 200))}` : ''}</li>`).join('')}</ul>`);
        }
      }
      const label = (b) => (b.count && String(b.label).includes('{count}')
        ? b.label.replace('{count}', String(statValue(b.count, ctx) ?? '')).replace(/\s{2,}/g, ' ') : b.label);
      const buttons = head.buttons.map((b) => buttonTag(b, label(b), ctx)).join(' ');
      if (buttons) part.push(`<p>${buttons}</p>`);
      if (part.length) out.push(`<section>${part.join('')}</section>`);
    });
  } else {
    out.push(`<h1>${escapeHtml(row.title || '')}</h1>`);
    if (row.description) out.push(paragraphs(row.description));
    if (row.page_markdown) out.push(paragraphs(row.page_markdown));
    if (row.url) out.push(`<p>${linkTag(row.url, row.url, true)}</p>`);
  }
  out.push('</main>');
  const footer = navLinks(cf(site).footer_links);
  if (footer) out.push(`<footer>${footer}</footer>`);
  return out.join('');
}

/**
 * The built site.html with this page put in: its title and head tags after <title>, the boot script
 * (the page's data) straight after the charset — before site.html's own early fetch, which stands down
 * when the data is already there — and the readable copy right after the root element.
 * Replacements are functions so a `$` in the data is never read as a pattern.
 */
export function fillShell(shell, { title = '', headTags: tags = '', boot = '', noscript = '' }) {
  return String(shell)
    .replace(/<title>[\s\S]*?<\/title>/i, () => `<title>${escapeHtml(title)}</title>${tags}`)
    .replace(/(<meta charset="[^"]*"\s*\/?>)/i, (m) => `${m}${boot}`)
    .replace(/(<div id="site-root"><\/div>)/i, (m) => `${m}${noscript}`);
}
